import { canv } from "@/canv/global";
import { sortByIndexByParent } from "@/canvasController/helpers";
import type { ElementBase } from "@/elementBase";
import { frameWidget } from "@/elements/frame";
import type { Group } from "@/group";
import type { Area } from "@/group/types";
import { MAX_TEXT_SIZE, MIN_TEXT_SIZE } from "./constants";
import i18n, { normalizeLanguage } from "./i18n";

export const isMobile = () => {
    return /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(navigator.userAgent);
};

export const isSafari = () => {
    if (typeof navigator === "undefined") return false;
    const userAgent = navigator.userAgent.toLowerCase();

    return (
        userAgent.includes("safari") &&
        !userAgent.includes("chrome") &&
        !userAgent.includes("chromium") &&
        !userAgent.includes("edg")
    );
};

const getDateLocale = () => {
    const language = normalizeLanguage(i18n.language);

    return language === "en" ? "en-US" : "ru-RU";
};

export const capitalize = (value: string) => {
    if (!value) return value;

    return value.charAt(0).toUpperCase() + value.slice(1);
};
export const getDate = (date: Date) => {
    const minutes = date.getMinutes();
    const hours = date.getHours();
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const locale = getDateLocale();

    if (isToday(date)) {
        return `${padZero(hours)}:${padZero(minutes)}`;
    }

    if (isCurrentWeek(date)) {
        const weekDay = capitalize(
            new Intl.DateTimeFormat(locale, {
                weekday: "long",
            }).format(date),
        );

        return `${weekDay}, ${padZero(hours)}:${padZero(minutes)}`;
    }

    if (isCurrentYear(date)) {
        const dayMonth = new Intl.DateTimeFormat(locale, {
            day: "2-digit",
            month: "long",
        }).format(date);

        return `${dayMonth}, ${padZero(hours)}:${padZero(minutes)}`;
    }

    return `${padZero(day)}.${padZero(month + 1)}.${year}`;
};

const padZero = (num: number) => {
    return num < 10 ? `0${num}` : num.toString();
};

export const formatSecondsToMMSS = (seconds: number): string => {
    const min = Math.floor(seconds / 60);
    const sec = seconds - min * 60;

    return `${padZero(min)}:${padZero(sec)}`;
};

const isToday = (date: Date) => {
    const today = new Date();

    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
};

const isCurrentWeek = (date: Date) => {
    const today = new Date();

    const weekDay = (today.getDay() + 6) % 7;
    const monthDay = today.getDate();
    const mondayThisWeek = monthDay - weekDay;

    const startOfThisWeek = new Date();
    startOfThisWeek.setDate(mondayThisWeek);
    startOfThisWeek.setHours(0, 0, 0, 0);

    const startOfNextWeek = new Date(+startOfThisWeek);
    startOfNextWeek.setDate(mondayThisWeek + 7);

    return date >= startOfThisWeek && date < startOfNextWeek;
};

const isCurrentYear = (date: Date) => {
    const today = new Date();

    return date.getFullYear() === today.getFullYear();
};

export const getFontSize = (value: number) => {
    return Math.max(MIN_TEXT_SIZE, Math.min(MAX_TEXT_SIZE, value));
};

export const getScaledSize = (size: number, k = 2.5) => {
    const transform = canv.ctx.getTransform();
    const scaleX = transform.a;
    const scaleY = transform.d;

    let scale = Math.max(scaleX, scaleY);
    // ограничение масштаба, чтобы при максимальных отдалениях не перекрывались виджеты
    if (scale < 0.5) {
        scale = 0.5;
    }
    return (size * k) / scale;
};

export const getScale = () => {
    const transform = canv.ctx.getTransform();
    const scaleX = transform.a;
    const scaleY = transform.d;

    return Math.max(scaleX, scaleY);
};

export const getGroupsWithOverlapped = (
    groups: Group[],
    baseX: number,
    baseY: number,
    withGhost = false,
) => {
    const set = new Set();
    const result: { groups: Group[]; ghosts: { dx: number; dy: number; w: number; h: number }[] } =
        {
            groups: [],
            ghosts: [],
        };

    const getGroupGhost = (group: Group) => {
        return {
            dx: group.startX - baseX,
            dy: group.startY - baseY,
            w: group.width,
            h: group.height,
        };
    };

    const addToResult = (group: Group, addGroup = true, addGhost = true) => {
        if (set.has(group.UID)) return;

        set.add(group.UID);
        if (addGroup) result.groups.push(group);
        if (addGhost && withGhost) result.ghosts.push(getGroupGhost(group));
    };

    for (const group of groups) {
        // вычисление групп на фрейме
        // если его нет в children[0], значит он в рамках объединения, а там вычисления не требуются
        if (group.children[0] instanceof frameWidget) {
            addToResult(group);

            const frame = group.children[0] as frameWidget;
            const overlappedGroups = frame.getOverlappedGroups();

            if (overlappedGroups.length) {
                for (const overlappedGroup of overlappedGroups) {
                    addToResult(overlappedGroup);
                }
            }
        } else {
            const isItemWrapper = group.isItemWrapper();
            addToResult(group, true, isItemWrapper);

            if (!isItemWrapper && withGhost) {
                for (let j = 0; j < group.children.length; j++) {
                    addToResult(group.children[j] as Group, false);
                }
            }
        }
    }

    return result;
};

const getGroupsAndUidsFromGroup = (root: Group): { groups: Group[]; groupsUids: string[] } => {
    const groups: Group[] = [];
    const groupsUids: string[] = [];
    const stack: Group[] = [root];

    while (stack.length) {
        const currentGroup = stack.pop();
        if (!currentGroup) break;

        if (currentGroup.isItemWrapper()) {
            groups.push(currentGroup);
            groupsUids.push(currentGroup.UID);
            continue;
        }

        const kids = currentGroup.children;
        for (let i = kids.length - 1; i >= 0; i--) {
            stack.push(kids[i] as Group);
        }
    }

    return { groups, groupsUids };
};

export const queryAreaByGroup = (
    group: Group,
    withSort = true,
    isAnchorOnly = false,
    excludeConstructorNames: string[] | null = null,
) => {
    if (!canv.canvasController.hashGrid) return [];

    const area = {
        startX: group.startX,
        startY: group.startY,
        width: group.width || 1,
        height: group.height || 1,
    };

    const { groups, groupsUids } = getGroupsAndUidsFromGroup(group);

    const areaCells = canv.canvasController.hashGrid.getCellsForElement(area);
    const found = new Set<Group>();

    const intersects = (g: Group) => {
        const areaRight = area.startX + area.width;
        const areaBottom = area.startY + area.height;
        const groupRight = g.startX + g.width;
        const groupBottom = g.startY + g.height;

        return !(
            groupRight < area.startX ||
            g.startX > areaRight ||
            groupBottom < area.startY ||
            g.startY > areaBottom
        );
    };

    const isAnchor = (cellGroup: Group) => {
        return groups.some((g) =>
            g.children.some(
                (children) => (cellGroup.children[0] as ElementBase).anchorUID === children.UID,
            ),
        );
    };

    for (const cell of areaCells) {
        const cellGroups = canv.canvasController.hashGrid.grid.get(cell);
        if (!cellGroups) continue;

        for (const cellGroup of cellGroups) {
            // исключаем саму группу, она служит просто областью для поиска
            if (groupsUids.includes(cellGroup.UID)) continue;
            // опционально исключаем какие-либо виджеты по constructorName
            if (
                excludeConstructorNames?.includes(
                    (cellGroup.children[0] as ElementBase).constructorName,
                )
            )
                continue;
            if (!intersects(cellGroup)) continue;
            // опционально получаем только группы в которых есть якорь на виджет из переданной группы в параметрах
            if (isAnchorOnly && !isAnchor(cellGroup)) continue;

            found.add(cellGroup);
        }
    }

    const result = Array.from(found);
    if (withSort && result.length) sortByIndexByParent(result);

    return result;
};

export const isInside = (outer: Area, inner: Area) => {
    return (
        inner.x >= outer.x &&
        inner.y >= outer.y &&
        inner.x + inner.width <= outer.x + outer.width &&
        inner.y + inner.height <= outer.y + outer.height
    );
};

export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(1)} GB`;
};

// TODO: т.к. мы разделили canvasController.items на items и groups эта функция пока будет проверкой
// для множества мест где применяется canvasController.items что бы сразу знать где у нас произошла утечка групп
export const isItemWrapperGroup = (group: Group) => {
    if (group.isItemWrapper()) {
        return true;
    } else {
        throw new Error("В canv.canvasController.items попала группа не являющаяся itemWrapper");
    }
};
